import React, { useEffect, useMemo, useState } from "react";
import { KeyboardBackspace } from "@mui/icons-material";
import { Switch } from "antd";
import { CircularProgress, LinearProgress } from "@mui/material";
import JobSwmsFormV3Shell from "./JobSwmsFormV3Shell.jsx";
import JobSwmsV3PreStartContainer from "./pre-start/JobSwmsV3PreStartContainer.jsx";
import JobSwmsV3EmergencyPlansContainer from "./emergency-plans/JobSwmsV3EmergencyPlansContainer.jsx";
import { normalizeEmergencyPlans } from "./emergency-plans/emergencyPlanAdapter";
import JobSwmsV3EmergencyProceduresContainer from "./emergency-procedures/JobSwmsV3EmergencyProceduresContainer.jsx";
import JobSwmsV3RescueReadinessContainer from "./rescue-readiness/JobSwmsV3RescueReadinessContainer.jsx";
import PreStartToggleConfirmModal from "./pre-start/PreStartToggleConfirmModal.jsx";
import JobSwmsV3AddEditPreStartQuestionModal from "./pre-start/JobSwmsV3AddEditPreStartQuestionModal.jsx";
import usePreStartQuestionDraft from "./pre-start/hooks/usePreStartQuestionDraft";
import usePreStartQuestionModalState from "./pre-start/hooks/usePreStartQuestionModalState";
import usePreStartChecklistApi from "./pre-start/usePreStartChecklistApi";
import {
  normalizePreStartChecklist,
  resolveMethodOptions,
  // resolvePreStartHeadingContent
} from "./pre-start/utils/preStartChecklistV3.utils";
import preStartStyles from "./pre-start/JobSwmsFormV3PreStart.module.css";
import { serializeInitialValuesV3 } from "./SwmsFormV3Serializer";
import commonStyles from "../../../JobStyle.module.css";

const EMPTY_SWMS_DATA = {};

const isEnabledValue = (value) => value === true || value === 1 || value === "1";

const normalizeEmergencyServerState = (response, collectionKey) => {
  const payload = response || {};
  const document = payload.document || {};
  const data = Array.isArray(document[collectionKey]) ? document[collectionKey] : [];
  const toggleValue = payload.toggle !== undefined ? payload.toggle : document.toggle;
  const toggle = isEnabledValue(toggleValue);

  return {
    loading: false,
    error: payload.error || null,
    exists: isEnabledValue(payload.exists),
    toggle,
    data
  };
};

export default function JobSwmsFormV3Container({ onClose, swms, jobId }) {
  const TAB_SWMS = "swms";
  const TAB_PRE_START = "prestart";
  const TAB_EMERGENCY_PLANS = "emergency-plans";
  const TAB_EMERGENCY_PROCEDURES = "emergency-procedures";
  const TAB_RESCUE_READINESS = "rescue-readiness";

  const [activeTab, setActiveTab] = useState(TAB_SWMS);
  const [preStartToggle, setPreStartToggle] = useState(false);
  const [emergencyPlansToggle, setEmergencyPlansToggle] = useState(false);
  const [emergencyProceduresToggle, setEmergencyProceduresToggle] = useState(false);
  const [rescueReadinessToggle, setRescueReadinessToggle] = useState(false);

  const resolvedJobId = jobId 
    || (swms && swms.version && swms.version.job_id)
    || (swms && swms.task && (swms.task.job_id || swms.task.jobId));
  const resolvedSwmsId = (swms && swms.version && swms.version.swms_form_id) || (swms && swms.swms_form_id);
  const {
    formData,
    categories,
    methodItemTypeGroups,
    loadingData,
    refreshingAfterSave,
    checklistRequestInFlight,
    updatingPreStartToggle,
    pendingPreStartToggle,
    toggleConfirmVisible,
    loadFormData,
    abortRequests,
    closeToggleConfirmModal,
    openToggleConfirmModal,
    handleSaveQuestions: handleSaveQuestionsApi,
    handleConfirmPreStartToggle: handleConfirmPreStartToggleApi
  } = usePreStartChecklistApi({
    onClose,
    resolvedJobId,
    resolvedSwmsId,
    activeTab,
    tabPreStart: TAB_PRE_START,
    preStartToggle,
    onPreStartToggleChange: setPreStartToggle
  });
  const swmsData = formData && formData.swms_data ? formData.swms_data : EMPTY_SWMS_DATA;
  const emergencyServerState = useMemo(() => ({
    plans: normalizeEmergencyServerState(swmsData && swmsData.emergency_plans, "plans"),
    procedures: normalizeEmergencyServerState(swmsData && swmsData.emergency_procedures, "procedures"),
    readiness: normalizeEmergencyServerState(swmsData && swmsData.emergency_readiness, "questions")
  }), [swmsData]);
  const emergencyLoading = loadingData || refreshingAfterSave;

  const normalizedPreStartChecklist = useMemo(
    () => normalizePreStartChecklist(swmsData),
    [swmsData]
  );

  // const preStartHeadingContent = useMemo(
  //   () => resolvePreStartHeadingContent(swmsData),
  //   [swmsData]
  // );
  const {
    savedPreStartChecklist,
    workingPreStartChecklist,
    hasUnsavedQuestionChanges,
    questionCount: preStartQuestionCount,
    selectedQuestionByIndex,
    resetWorkingToSaved,
    replaceDraftWithServerChecklist,
    applyToggleServerChecklist,
    moveQuestionRow,
    onAfterDropQuestionRow,
    applyQuestionModalSubmit,
    updateHeadingContent,
    updateStaffSignoffHeadingContent
  } = usePreStartQuestionDraft({
    swmsId: resolvedSwmsId,
    initialChecklist: normalizedPreStartChecklist
  });
  const {
    questionModalVisible,
    questionModalMode,
    selectedQuestionIndex,
    closeQuestionModal,
    openAddQuestionModal,
    openEditQuestionModal
  } = usePreStartQuestionModalState();
  const methodOptions = useMemo(() => resolveMethodOptions(swmsData), [swmsData]);
  const selectedQuestion = selectedQuestionByIndex(selectedQuestionIndex);
  const preStartInteractionDisabled = checklistRequestInFlight || refreshingAfterSave;

  useEffect(() => {
    setPreStartToggle(normalizedPreStartChecklist.toggle === 1);
  }, [normalizedPreStartChecklist]);

  useEffect(() => {
    setEmergencyPlansToggle(emergencyServerState.plans.toggle);
    setEmergencyProceduresToggle(emergencyServerState.procedures.toggle);
    setRescueReadinessToggle(emergencyServerState.readiness.toggle);
  }, [emergencyServerState]);

  const handleEmergencyPlansToggle = (checked) => {
    const included = Boolean(checked) && emergencyServerState.plans.data.length > 0;
    setEmergencyPlansToggle(included);

    if (!included) {
      setEmergencyProceduresToggle(false);
      setRescueReadinessToggle(false);
    }
  };

  const handleEmergencyProceduresToggle = (checked) => {
    setEmergencyProceduresToggle(Boolean(checked) && emergencyServerState.procedures.data.length > 0);
  };

  const handleRescueReadinessToggle = (checked) => {
    setRescueReadinessToggle(Boolean(checked) && emergencyServerState.readiness.data.length > 0);
  };

  const handleOpenAddQuestionModal = () => {
    openAddQuestionModal({ disabled: preStartInteractionDisabled });
  };

  const handleOpenEditQuestionModal = (_, index) => {
    openEditQuestionModal(index, { disabled: preStartInteractionDisabled });
  };

  const handleMoveQuestionRow = (fromIndex, toIndex) => {
    moveQuestionRow(fromIndex, toIndex, { disabled: preStartInteractionDisabled });
  };

  const handleAfterDropQuestionRow = (payload = {}) => {
    onAfterDropQuestionRow(payload, { disabled: preStartInteractionDisabled });
  };

  const handleSaveQuestions = () => {
    handleSaveQuestionsApi({
      savedPreStartChecklist,
      workingPreStartChecklist,
      hasUnsavedQuestionChanges,
      replaceDraftWithServerChecklist
    });
  };

  const handleCancelQuestions = () => {
    resetWorkingToSaved({ disabled: preStartInteractionDisabled });
  };

  const handleQuestionModalSubmit = (payload = {}) => {
    applyQuestionModalSubmit(payload);

    closeQuestionModal();
  };

  const handleConfirmPreStartToggle = () => {
    handleConfirmPreStartToggleApi({
      savedPreStartChecklist,
      applyToggleServerChecklist
    });
  };

  const handleBackToSwms = () => {
    if (activeTab === TAB_PRE_START && hasUnsavedQuestionChanges) {
      const shouldDiscard = window.confirm("You have unsaved Pre-Start checklist changes. Discard changes and go back?");
      if (!shouldDiscard) {
        return;
      }

      resetWorkingToSaved();
    }

    onClose(null);
  };

  useEffect(() => {
    loadFormData();

    return () => {
      abortRequests();
    };
  }, []);

  useEffect(() => {
    setActiveTab(TAB_SWMS);
  }, [resolvedSwmsId]);

  if (loadingData) {
    return (
      <div className="col-12 d-flex align-items-center justify-content-center p-5">
        <CircularProgress size={20} />
      </div>
    );
  }

  return (
    <div className={preStartStyles.FormTabsWrap}>
      {refreshingAfterSave && <LinearProgress />}

      <div className="px-2">
        <button
          className={`${commonStyles.BlueTextButton} px-2 ${commonStyles.PrimaryText}`}
          type="button"
          onClick={handleBackToSwms}
        >
          <KeyboardBackspace /> Back to SWMS
        </button>
      </div>

      <div
        className={preStartStyles.FormTabs}
        role="tablist"
        aria-label="SWMS form tabs"
      >
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === TAB_SWMS}
          className={`${preStartStyles.FormTabButton} ${activeTab === TAB_SWMS ? preStartStyles.FormTabButtonActive : preStartStyles.FormTabButtonInactive}`.trim()}
          onClick={() => setActiveTab(TAB_SWMS)}
        >
          SWMS
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === TAB_PRE_START}
          className={`${preStartStyles.FormTabButton} ${activeTab === TAB_PRE_START ? preStartStyles.FormTabButtonActive : preStartStyles.FormTabButtonInactive}`.trim()}
          onClick={() => setActiveTab(TAB_PRE_START)}
        >
          <span className={preStartStyles.FormTabLabel}>Pre-Start</span>
          <span
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <Switch
              className="cs-switch-v2"
              disabled={activeTab !== TAB_PRE_START || checklistRequestInFlight}
              checked={preStartToggle}
              onChange={openToggleConfirmModal}
              size="small"
            />
          </span>
          <span
            className={`${preStartStyles.FormTabMetaPill} ${activeTab === TAB_PRE_START ? "" : preStartStyles.FormTabMetaPillInactive}`.trim()}
          >
            {preStartQuestionCount} {preStartQuestionCount === 1 ? "question" : "questions"}
          </span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === TAB_EMERGENCY_PLANS}
          className={`${preStartStyles.FormTabButton} ${activeTab === TAB_EMERGENCY_PLANS ? preStartStyles.FormTabButtonActive : preStartStyles.FormTabButtonInactive}`.trim()}
          onClick={() => setActiveTab(TAB_EMERGENCY_PLANS)}
        >
          <span className={preStartStyles.FormTabLabel}>Emergency Plans</span>
          <span
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <Switch
              className="cs-switch-v2"
              checked={emergencyPlansToggle}
              disabled={emergencyServerState.plans.data.length === 0}
              onChange={handleEmergencyPlansToggle}
              size="small"
            />
          </span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === TAB_EMERGENCY_PROCEDURES}
          className={`${preStartStyles.FormTabButton} ${activeTab === TAB_EMERGENCY_PROCEDURES ? preStartStyles.FormTabButtonActive : preStartStyles.FormTabButtonInactive}`.trim()}
          onClick={() => setActiveTab(TAB_EMERGENCY_PROCEDURES)}
        >
          <span className={preStartStyles.FormTabLabel}>Emergency Procedures</span>
          <span
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <Switch
              className="cs-switch-v2"
              checked={emergencyProceduresToggle}
              disabled={emergencyServerState.procedures.data.length === 0}
              onChange={handleEmergencyProceduresToggle}
              size="small"
            />
          </span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === TAB_RESCUE_READINESS}
          className={`${preStartStyles.FormTabButton} ${activeTab === TAB_RESCUE_READINESS ? preStartStyles.FormTabButtonActive : preStartStyles.FormTabButtonInactive}`.trim()}
          onClick={() => setActiveTab(TAB_RESCUE_READINESS)}
        >
          <span className={preStartStyles.FormTabLabel}>Emergency Readiness</span>
          <span
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <Switch
              className="cs-switch-v2"
              checked={rescueReadinessToggle}
              disabled={emergencyServerState.readiness.data.length === 0}
              onChange={handleRescueReadinessToggle}
              size="small"
            />
          </span>
        </button>
      </div>

      <div
        className={activeTab === TAB_SWMS ? preStartStyles.FormTabPaneActive : preStartStyles.FormTabPaneHidden}>
        <JobSwmsFormV3Shell
          onClose={onClose}
          swmsData={swmsData}
          jobId={resolvedJobId}
          approvalSubmissionHistory={formData && formData.approvalSubmissionHistory ? formData.approvalSubmissionHistory : []}
          swmsTitle={formData && formData.title ? formData.title : ""}
          swmsNumber={formData && formData.swmsNumber ? formData.swmsNumber : ""}
          swmsStatus={formData && formData.currentStatus ? formData.currentStatus : null}
          swmsId={resolvedSwmsId}
          refreshForm={loadFormData}
          initialValues={serializeInitialValuesV3(swmsData)}
        />
      </div>

      <div
        className={activeTab === TAB_PRE_START ? preStartStyles.FormTabPaneActive : preStartStyles.FormTabPaneHidden}>
        <JobSwmsV3PreStartContainer
          checklist={workingPreStartChecklist}
          initialValues={{
            prestart_checklist_heading: workingPreStartChecklist
              && workingPreStartChecklist.data
              && typeof workingPreStartChecklist.data.prestart_checklist_heading === "string"
              ? workingPreStartChecklist.data.prestart_checklist_heading
              : "",
            prestart_staff_signoff: workingPreStartChecklist
              && workingPreStartChecklist.data
              && typeof workingPreStartChecklist.data.prestart_staff_signoff === "string"
              ? workingPreStartChecklist.data.prestart_staff_signoff
              : ""
          }}
          onOpenAddQuestion={handleOpenAddQuestionModal}
          onOpenEditQuestion={handleOpenEditQuestionModal}
          moveQuestionRow={handleMoveQuestionRow}
          onAfterDropQuestionRow={handleAfterDropQuestionRow}
          onCancelQuestions={handleCancelQuestions}
          onSaveQuestions={handleSaveQuestions}
          onHeadingContentChange={updateHeadingContent}
          onStaffSignoffHeadingContentChange={updateStaffSignoffHeadingContent}
          hasUnsavedQuestionChanges={hasUnsavedQuestionChanges}
          questionInteractionDisabled={preStartInteractionDisabled}
          requestInFlight={preStartInteractionDisabled}
        />
      </div>

      <div
        className={activeTab === TAB_EMERGENCY_PLANS ? preStartStyles.FormTabPaneActive : preStartStyles.FormTabPaneHidden}
      >
        <JobSwmsV3EmergencyPlansContainer
            serverState={{ ...emergencyServerState.plans, loading: emergencyLoading }}
            included={emergencyPlansToggle}
            toggle={emergencyPlansToggle}
            initialValues={{ plans: emergencyServerState.plans.data.map((plan) => ({
              ...normalizeEmergencyPlans([plan])[0],
              category: plan.category
                ? {
                  label: plan.category.category_name || plan.category.name,
                  value: plan.category.id
                }
                : null,
              toggle: plan.toggle === undefined ? 1 : Number(plan.toggle)
            })) }}
            categories={categories}
          swmsStatus={formData && formData.currentStatus ? formData.currentStatus : ""}
          swmsData={swmsData}
          methodItemTypeGroups={methodItemTypeGroups}
          methodItemTypesLoading={emergencyLoading}
          onRetry={() => loadFormData()}
          />
      </div>

      <div
        className={activeTab === TAB_EMERGENCY_PROCEDURES ? preStartStyles.FormTabPaneActive : preStartStyles.FormTabPaneHidden}
      >
        <JobSwmsV3EmergencyProceduresContainer
            serverState={{ ...emergencyServerState.procedures, loading: emergencyLoading }}
            included={emergencyProceduresToggle}
            toggle={emergencyProceduresToggle}
            initialValues={emergencyServerState.procedures.data}
          onRetry={() => loadFormData()}
          />
      </div>

      <div
        className={activeTab === TAB_RESCUE_READINESS ? preStartStyles.FormTabPaneActive : preStartStyles.FormTabPaneHidden}
      >
        <JobSwmsV3RescueReadinessContainer
            serverState={{ ...emergencyServerState.readiness, loading: emergencyLoading }}
            included={rescueReadinessToggle}
            toggle={rescueReadinessToggle}
            initialValues={emergencyServerState.readiness.data}
          onRetry={() => loadFormData()}
          />
      </div>

      <PreStartToggleConfirmModal
        visible={toggleConfirmVisible}
        targetEnabled={Boolean(pendingPreStartToggle)}
        loading={updatingPreStartToggle}
        onCancel={closeToggleConfirmModal}
        onConfirm={handleConfirmPreStartToggle}
      />

      <JobSwmsV3AddEditPreStartQuestionModal
        visible={questionModalVisible}
        mode={questionModalMode}
        selectedQuestion={selectedQuestion}
        methodOptions={methodOptions}
        onCancel={closeQuestionModal}
        onSubmit={handleQuestionModalSubmit}
      />
    </div>
  );
}